import React, { useState } from "react";
import { Box, Button, Typography, styled, keyframes } from "@mui/material";
import { CalculatingFit } from "./CalculatingFit";
import type { MSHatData } from "../../types";
import type { FacetDefinition } from "../../../components/FacetInfoModal";

interface BackCenterPanelProps {
  hat: MSHatData;
  onFlip: () => void;
  cmsFacets?: FacetDefinition[];
  userHighInterestRiasec?: string[];
  userTopWorkValues?: string[];
}

// --- Animations ---

const sparkle = keyframes`
  0%, 100% { opacity: 0; transform: scale(0) rotate(0deg); }
  20% { opacity: 1; transform: scale(1) rotate(0deg); }
  50% { opacity: 1; transform: scale(2) rotate(20deg); }
  80% { opacity: 0.6; transform: scale(0.8) rotate(-10deg); }
`;

const twinkle = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(0.6); }
  50% { opacity: 1; transform: scale(1); }
`;

const glowPulse = keyframes`
  0%, 100% { box-shadow: 0 0 6px #fa680d; }
  50% { box-shadow: 0 0 12px #fa7c0d, 0 0 4px #FFD54F; }
`;

// --- Styled components ---

const PanelContainer = styled(Box, {
  label: "BackCenterPanelContainer",
})(() => ({
  height: "100%",
  aspectRatio: "368 / 569",
  flexShrink: 0,
  position: "relative",
}));

const CardContainer = styled(Box, {
  label: "BackCenterCard",
})(() => ({
  position: "relative",
  width: "100%",
  height: "100%",
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: "rgba(145, 158, 171, 0.2)",
  backdropFilter: "blur(30px)",
  display: "flex",
  flexDirection: "column",
  padding: "16px 24px",
  gap: 16,
  alignItems: "center",
}));

const CardTitle = styled(Typography, {
  label: "BackCenterTitle",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "18px",
  lineHeight: "28px",
  color: "#ffffff",
  textAlign: "center",
  width: "100%",
}));

const SectionLabel = styled(Typography, {
  label: "BackCenterSectionLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#ffffff",
  textTransform: "uppercase",
  width: "100%",
}));

const CoreGrid = styled(Box, {
  label: "BackCenterCoreGrid",
})(() => ({
  display: "flex",
  flexDirection: "column",
  gap: 8,
  width: "100%",
}));

const CoreRow = styled(Box, {
  label: "BackCenterCoreRow",
})(() => ({
  display: "flex",
  gap: 24,
  width: "100%",
}));

const CoreItem = styled(Box, {
  label: "BackCenterCoreItem",
  shouldForwardProp: (prop) => prop !== "isHigh" && prop !== "facetColor",
})<{ isHigh?: boolean; facetColor?: string }>(({ isHigh, facetColor }) => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "6px 8px",
  borderRadius: 8,
  flex: 1,
  backgroundColor: isHigh ? facetColor || "#c684ff" : "#c4cdd5",
}));

const CoreIcon = styled(Box, {
  label: "BackCenterCoreIcon",
  shouldForwardProp: (prop) => prop !== "isMatch",
})<{ isMatch?: boolean }>(({ isMatch }) => ({
  width: 48,
  height: 48,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
  borderRadius: "50%",
  ...(isMatch && {
    animation: `${glowPulse} 2s ease-in-out infinite`,
  }),
  "& img": {
    width: 48,
    height: 48,
    objectFit: "contain",
  },
}));

const CoreInfo = styled(Box, {
  label: "BackCenterCoreInfo",
})(() => ({
  display: "flex",
  flexDirection: "column",
}));

const InterestLevel = styled(Typography, {
  label: "BackCenterInterestLevel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#1c252e",
}));

const DimensionName = styled(Typography, {
  label: "BackCenterDimensionName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  lineHeight: "22px",
  color: "rgba(0, 0, 0, 0.8)",
}));

const WorkValuesRow = styled(Box, {
  label: "BackCenterWorkValuesRow",
})(() => ({
  display: "flex",
  gap: 24,
  width: "100%",
}));

const WorkValueItem = styled(Box, {
  label: "BackCenterWorkValueItem",
})(() => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: "center",
  position: "relative",
}));

const WorkValueIconContainer = styled(Box, {
  label: "BackCenterWorkValueIcon",
  shouldForwardProp: (prop) => prop !== "isMatch",
})<{ isMatch?: boolean }>(({ isMatch }) => ({
  width: 48,
  height: 48,
  borderRadius: "100%",
  backgroundColor: "#c684ff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  position: "relative",
  ...(isMatch && {
    animation: `${glowPulse} 2s ease-in-out infinite`,
  }),
  "& img": {
    width: 48,
    height: 48,
    objectFit: "contain",
    borderRadius: "100%",
  },
}));

const WorkValueName = styled(Typography, {
  label: "BackCenterWorkValueName",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 400,
  fontSize: "12px",
  lineHeight: "18px",
  color: "#ffffff",
  whiteSpace: "nowrap",
}));

const MatchText = styled(Typography, {
  label: "BackCenterMatchText",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontStyle: "italic",
  fontWeight: 400,
  fontSize: "14px",
  lineHeight: "22px",
  color: "#fff5cc",
  textAlign: "center",
}));

const SeeYourFitButton = styled(Button, {
  label: "BackCenterSeeYourFit",
})(() => ({
  color: "#ffffff",
  borderColor: "rgba(255, 255, 255, 0.32)",
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  textTransform: "none",
  borderRadius: "8px",
  paddingLeft: 24,
  paddingRight: 24,
  "&:hover": {
    borderColor: "rgba(255, 255, 255, 0.48)",
    backgroundColor: "rgba(255, 255, 255, 0.08)",
  },
}));

const FlipButton = styled("button", {
  label: "BackCenterFlipButton",
})(() => ({
  border: "none",
  padding: 0,
  outline: "none",
  "&:focus-visible": {
    outline: "2px solid #fa680d",
    outlineOffset: 2,
  },
  position: "absolute",
  bottom: 24,
  left: "50%",
  transform: "translateX(-50%)",
  width: 64,
  height: 64,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.32)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "background-color 0.2s ease",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.48)",
  },
}));

const FlipLabel = styled(Typography, {
  label: "BackCenterFlipLabel",
})(() => ({
  fontFamily: "Proxima Nova, sans-serif",
  fontWeight: 600,
  fontSize: "16px",
  lineHeight: "24px",
  color: "#ffffff",
  textAlign: "center",
}));

const FlipIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M17 1l4 4-4 4"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3 11V9a4 4 0 0 1 4-4h14"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M7 23l-4-4 4-4"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M21 13v2a4 4 0 0 1-4 4H3"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <path
      d="M9 1L11.5 6L17 7L13 11L14 17L9 14L4 17L5 11L1 7L6.5 6L9 1Z"
      stroke="#fff"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
  </svg>
);

// Sparkle stars for matched work values
const SparkleContainer = styled(Box, {
  label: "SparkleContainer",
})(() => ({
  position: "absolute",
  inset: "-12px",
  pointerEvents: "none",
}));

const Star = styled("svg", {
  label: "SparkleStar",
})<{ delay: number; x: string; y: string }>(({ delay, x, y }) => ({
  position: "absolute",
  left: x,
  top: y,
  filter: "drop-shadow(0 0 1px rgb(236, 236, 236))",
  animation: `${sparkle} 2.4s ${delay}s ease-in-out infinite`,
}));

const Dot = styled(Box, {
  label: "SparkleDot",
})<{ delay: number; x: string; y: string }>(({ delay, x, y }) => ({
  position: "absolute",
  left: x,
  top: y,
  width: 3,
  height: 3,
  borderRadius: "50%",
  backgroundColor: "#fa830d",
  boxShadow: "0 0 2px rgba(0, 0, 0, 0.4)",
  animation: `${twinkle} 1.8s ${delay}s ease-in-out infinite`,
}));

const Sparkles = () => (
  <SparkleContainer>
    <Star delay={0} x="0" y="20%" width="10" height="10" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Star delay={0.8} x="75%" y="0" width="8" height="8" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Star delay={1.6} x="85%" y="70%" width="7" height="7" viewBox="0 0 10 10">
      <path d="M5 0L6 4L10 5L6 6L5 10L4 6L0 5L4 4Z" fill="#fa830d" />
    </Star>
    <Dot delay={0.4} x="15%" y="75%" />
    <Dot delay={1.2} x="60%" y="85%" />
  </SparkleContainer>
);

// --- Data mappings ---

const RIASEC_SCORE_TO_FACET: Record<string, string> = {
  realistic: "RIASEC:R",
  investigative: "RIASEC:I",
  artistic: "RIASEC:A",
  social: "RIASEC:S",
  enterprising: "RIASEC:E",
  conventional: "RIASEC:C",
};

const WORK_VALUE_SCORE_TO_FACET: Record<string, string> = {
  achievement: "Accomplishing Goals",
  independence: "Being Independent",
  recognition: "Getting Recognition",
  relationships: "Building Relationships",
  support: "Trust & Support",
  workingConditions: "Stability & Security",
};

const RIASEC_KEYS = Object.keys(RIASEC_SCORE_TO_FACET);
const WORK_VALUE_KEYS = Object.keys(WORK_VALUE_SCORE_TO_FACET);

export const BackCenterPanel = ({
  hat,
  onFlip,
  cmsFacets = [],
  userHighInterestRiasec,
  userTopWorkValues,
}: BackCenterPanelProps) => {
  const [showFit, setShowFit] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const handleSeeYourFit = () => {
    setCalculating(true);
  };

  const handleCalculatingComplete = () => {
    setCalculating(false);
    setShowFit(true);
  };

  const facetMap = new Map(cmsFacets.map((f) => [f.facetCode, f]));

  // Top 4 RIASEC for the hat, mark top 2 as "High Interest"
  const riasecEntries = RIASEC_KEYS.map((key) => ({
    key,
    score: hat.riasecScores?.[key] || 0,
    facet: facetMap.get(RIASEC_SCORE_TO_FACET[key]),
  }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 4);

  const highThreshold = riasecEntries.length >= 2 ? riasecEntries[1].score : 0;

  // Top 2 work values for the hat
  const workValueEntries = WORK_VALUE_KEYS.map((key) => ({
    key,
    score: hat.workValues?.[key] || 0,
    facet: facetMap.get(WORK_VALUE_SCORE_TO_FACET[key]),
  }))
    .sort((a, b) => b.score - a.score)
    .slice(0, 2);

  // Compute matches (only used after "See Your Fit" is clicked)
  const userRiasecSet = new Set(userHighInterestRiasec || []);
  const userWorkValueSet = new Set(userTopWorkValues || []);

  const hatHighRiasecKeys = riasecEntries
    .filter((e) => e.score >= highThreshold && e.score > 0)
    .map((e) => e.key);
  const hatTopWorkValueKeys = workValueEntries.map((e) => e.key);

  const riasecMatches = hatHighRiasecKeys.filter((k) => userRiasecSet.has(k));
  const workValueMatches = hatTopWorkValueKeys.filter((k) =>
    userWorkValueSet.has(k),
  );
  const totalMatches = riasecMatches.length + workValueMatches.length;
  const totalItems = hatHighRiasecKeys.length + hatTopWorkValueKeys.length;

  const riasecMatchSet = new Set(riasecMatches);
  const workValueMatchSet = new Set(workValueMatches);

  const matchNames: string[] = [
    ...riasecMatches.map((k) => {
      const entry = riasecEntries.find((e) => e.key === k);
      return entry?.facet?.variable || k;
    }),
    ...workValueMatches.map((k) => {
      const entry = workValueEntries.find((e) => e.key === k);
      return entry?.facet?.variable || entry?.facet?.longLabel || k;
    }),
  ];

  const hasUserData =
    (userHighInterestRiasec && userHighInterestRiasec.length > 0) ||
    (userTopWorkValues && userTopWorkValues.length > 0);

  return (
    <PanelContainer>
      <CardContainer>
        <CardTitle>Hat Profile</CardTitle>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
          }}
        >
          <SectionLabel>Core</SectionLabel>
          <CoreGrid>
            {[0, 2].map((startIdx) => (
              <CoreRow key={startIdx}>
                {riasecEntries.slice(startIdx, startIdx + 2).map((dim) => {
                  const isHigh = dim.score >= highThreshold && dim.score > 0;
                  const isMatch = showFit && riasecMatchSet.has(dim.key);
                  const label = dim.facet?.variable || dim.key;
                  return (
                    <CoreItem
                      key={dim.key}
                      isHigh={isHigh}
                      facetColor={dim.facet?.secondaryColor}
                    >
                      <CoreIcon isMatch={isMatch}>
                        {dim.facet?.icon ? (
                          <img
                            src={dim.facet.icon}
                            alt={label}
                            style={
                              !isHigh
                                ? { filter: "grayscale(100%)" }
                                : undefined
                            }
                          />
                        ) : (
                          <Typography
                            sx={{ fontSize: "20px", color: "#1c252e" }}
                          >
                            {label.charAt(0).toUpperCase()}
                          </Typography>
                        )}
                        {isMatch && <Sparkles />}
                      </CoreIcon>
                      <CoreInfo>
                        <InterestLevel>
                          {isHigh ? "High Interest" : "Low Interest"}
                        </InterestLevel>
                        <DimensionName>{label}</DimensionName>
                      </CoreInfo>
                    </CoreItem>
                  );
                })}
              </CoreRow>
            ))}
          </CoreGrid>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: "100%",
            flex: 1,
          }}
        >
          <SectionLabel>Work Values</SectionLabel>
          <WorkValuesRow>
            {workValueEntries.map((wv) => {
              const label = wv.facet?.variable || wv.facet?.longLabel || wv.key;
              const isMatch = showFit && workValueMatchSet.has(wv.key);
              return (
                <WorkValueItem key={wv.key}>
                  <WorkValueIconContainer isMatch={isMatch}>
                    {wv.facet?.icon ? (
                      <img src={wv.facet.icon} alt={label} />
                    ) : (
                      <Typography sx={{ fontSize: "16px", color: "#fff" }}>
                        {label.charAt(0).toUpperCase()}
                      </Typography>
                    )}
                    {isMatch && <Sparkles />}
                  </WorkValueIconContainer>
                  <WorkValueName>{label}</WorkValueName>
                </WorkValueItem>
              );
            })}
          </WorkValuesRow>

          {calculating ? (
            <CalculatingFit
              matchNames={matchNames}
              onComplete={handleCalculatingComplete}
            />
          ) : showFit && hasUserData ? (
            <Box sx={{ mt: 12, textAlign: "center" }}>
              <MatchText>
                You have {totalMatches} out of {totalItems} matches ✨
              </MatchText>
            </Box>
          ) : hasUserData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
              <SeeYourFitButton
                variant="outlined"
                onClick={handleSeeYourFit}
                startIcon={<StarIcon />}
              >
                See Your Fit
              </SeeYourFitButton>
            </Box>
          ) : null}
        </Box>

        <FlipButton onClick={onFlip} aria-label="Flip card to front">
          <FlipIcon />
          <FlipLabel>Flip</FlipLabel>
        </FlipButton>
      </CardContainer>
    </PanelContainer>
  );
};
